/**
 * SalesRegisterScreen — a chronological register of every sale invoice and
 * credit note in a period with its GST split; the document list a CA reconciles
 * GSTR-1 against.
 *
 * Computed entirely from local WatermelonDB — no network required.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import {
  presetRange,
  toDateInputValue,
  startOfDay,
  lastIncludedInstant,
  type RangePreset as Preset,
} from '@/utils/dateRange';
import { Button } from '@/components/ui/Button';
import { pdf, file } from '@/platform';
import { toCsv } from '@/utils/csv';
import { loadSalesRegister } from '@/reports/salesRegister/salesRegisterDataSource';
import { buildSalesRegister, signOf, type SalesRegister } from '@/reports/salesRegister/salesRegister';

// ─── Component ───────────────────────────────────────────────────────────────

export function SalesRegisterScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const [preset, setPreset] = useState<Preset>('month');
  const [customFrom, setCustomFrom] = useState(toDateInputValue(startOfDay(new Date())));
  const [customTo, setCustomTo]     = useState(toDateInputValue(new Date()));
  const [data, setData]             = useState<SalesRegister | null>(null);
  const [loading, setLoading]       = useState(true);
  const [exporting, setExporting]   = useState(false);

  const range = useCallback(
    (): [Date, Date] => presetRange(preset, { from: customFrom, to: customTo }),
    [preset, customFrom, customTo],
  );

  const load = useCallback(async () => {
    if (!activeStoreId) return;
    setLoading(true);
    try {
      const [from, to] = range();
      setData(buildSalesRegister(await loadSalesRegister(activeStoreId, from, to)));
    } finally {
      setLoading(false);
    }
  }, [activeStoreId, range]);

  useEffect(() => { void load(); }, [load]);

  const rangeStamp = (): string => {
    const [from, to] = range();
    return `${toDateInputValue(from)}_${toDateInputValue(lastIncludedInstant(to))}`;
  };

  const handleExportCsv = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const csv = toCsv(data.rows, [
        { header: 'Date', value: (r) => toDateInputValue(r.date) },
        { header: 'Invoice', value: (r) => r.invoiceNumber },
        { header: 'Type', value: (r) => (r.isCreditNote ? 'Credit note' : 'Sale') },
        { header: 'Customer', value: (r) => r.customerName },
        { header: 'GSTIN', value: (r) => r.gstin ?? '' },
        { header: 'Taxable', value: (r) => r.taxable },
        { header: 'CGST', value: (r) => r.cgst },
        { header: 'SGST', value: (r) => r.sgst },
        { header: 'IGST', value: (r) => r.igst },
        { header: 'Total', value: (r) => r.total },
        { header: 'Payment', value: (r) => (r.isCreditNote ? '' : r.paymentStatus) },
      ]);
      await file.saveText({
        content: csv,
        defaultFileName: `SalesRegister_${rangeStamp()}.csv`,
        filters: [{ name: 'CSV', extensions: ['csv'] }],
      });
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const [from, to] = range();
      await pdf.saveFromHtml({
        html: buildRegisterHtml(data, from, to),
        defaultFileName: `SalesRegister_${rangeStamp()}.pdf`,
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {(['today', 'week', 'month', 'custom'] as Preset[]).map((p) => (
          <button key={p} type="button" onClick={() => setPreset(p)}
            className={cn('rounded-md px-3 py-1.5 text-body transition-colors',
              preset === p ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle')}>
            {p === 'today' ? 'Today' : p === 'week' ? 'Last 7 days' : p === 'month' ? 'This month' : 'Custom'}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} max={customTo}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
            <span className="text-fg-tertiary">–</span>
            <input type="date" value={customTo} min={customFrom}
              onChange={(e) => setCustomTo(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="secondary" onClick={() => void handleExportCsv()} disabled={!data || exporting || data.rows.length === 0}>
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="secondary" onClick={() => void handleExportPdf()} disabled={!data || exporting || data.rows.length === 0}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : !data ? null : data.rows.length === 0 ? (
        <div className="mt-16 text-center text-body text-fg-tertiary">No sale documents in this period.</div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <KPI label="Net taxable" value={formatCurrency(data.totals.taxable)} />
            <KPI label="Net GST" value={formatCurrency(data.totals.totalTax)} />
            <KPI label="Net value" value={formatCurrency(data.totals.value)} accent />
            <KPI label="Documents" value={`${data.saleCount} + ${data.creditNoteCount} CN`} />
          </div>

          {/* Register table */}
          <Card title="Sales register">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] text-body">
                <thead>
                  <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
                    <th className="pb-1 text-left font-normal">Date</th>
                    <th className="pb-1 text-left font-normal">Invoice</th>
                    <th className="pb-1 text-left font-normal">Customer</th>
                    <th className="pb-1 text-right font-normal">Taxable</th>
                    <th className="pb-1 text-right font-normal">GST</th>
                    <th className="pb-1 text-right font-normal">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => {
                    const s = signOf(r.type);
                    return (
                      <tr key={i} className="border-b border-stroke-hairline/10">
                        <td className="py-1.5 text-fg-secondary tabular-nums">{r.date.toLocaleDateString('en-IN')}</td>
                        <td className="py-1.5">
                          <span className="text-fg-primary tabular-nums">{r.invoiceNumber}</span>
                          {r.isCreditNote && <span className="ml-1.5 rounded bg-warning/10 px-1 text-label text-warning-ink">CN</span>}
                        </td>
                        <td className="py-1.5 text-fg-secondary">{r.customerName}</td>
                        <td className="py-1.5 text-right tabular-nums text-fg-secondary">{formatCurrency(s * r.taxable)}</td>
                        <td className="py-1.5 text-right tabular-nums text-fg-tertiary">{formatCurrency(s * r.totalTax)}</td>
                        <td className={cn('py-1.5 text-right font-medium tabular-nums', r.isCreditNote ? 'text-warning-ink' : 'text-fg-primary')}>
                          {formatCurrency(s * r.total)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-stroke-hairline/20 font-medium">
                    <td className="pt-2 text-fg-primary" colSpan={3}>Net total</td>
                    <td className="pt-2 text-right tabular-nums text-fg-primary">{formatCurrency(data.totals.taxable)}</td>
                    <td className="pt-2 text-right tabular-nums text-fg-primary">{formatCurrency(data.totals.totalTax)}</td>
                    <td className="pt-2 text-right tabular-nums text-accent-ink">{formatCurrency(data.totals.value)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function KPI({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="text-label uppercase text-fg-tertiary">{label}</div>
      <div className={cn('mt-1 text-heading tabular-nums', accent ? 'text-accent-ink' : 'text-fg-primary')}>{value}</div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-3">
      <div className="mb-2 text-label uppercase text-fg-tertiary">{title}</div>
      {children}
    </div>
  );
}

// ─── PDF export ───────────────────────────────────────────────────────────────

function buildRegisterHtml(d: SalesRegister, from: Date, toExclusive: Date): string {
  const fmt = (n: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
  const fmtDate = (dt: Date) => dt.toLocaleDateString('en-IN');
  const to = lastIncludedInstant(toExclusive);
  const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] as string);

  const rows = d.rows.map((r) => {
    const s = signOf(r.type);
    return `<tr><td>${fmtDate(r.date)}</td><td>${esc(r.invoiceNumber)}${r.isCreditNote ? ' (CN)' : ''}</td><td>${esc(r.customerName)}</td>
     <td style="text-align:right">${fmt(s * r.taxable)}</td><td style="text-align:right">${fmt(s * r.totalTax)}</td>
     <td style="text-align:right">${fmt(s * r.total)}</td></tr>`;
  }).join('');

  return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:sans-serif;margin:32px;color:#222}h1{font-size:22px;margin-bottom:2px}
table{width:100%;border-collapse:collapse;font-size:11px;margin-top:10px}th,td{padding:5px 7px;border-bottom:1px solid #eee}
th{text-align:right;color:#666;font-weight:500}th:nth-child(-n+3),td:nth-child(-n+3){text-align:left}
tfoot td{border-top:2px solid #333;font-weight:700}</style></head>
<body>
<h1>Sales Register</h1>
<p style="color:#888">${fmtDate(from)} – ${fmtDate(to)} · ${d.saleCount} invoices + ${d.creditNoteCount} credit notes · Net ${fmt(d.totals.value)} (GST ${fmt(d.totals.totalTax)})</p>
<table>
<thead><tr><th>Date</th><th>Invoice</th><th>Customer</th><th style="text-align:right">Taxable</th><th style="text-align:right">GST</th><th style="text-align:right">Total</th></tr></thead>
<tbody>${rows}</tbody>
<tfoot><tr><td colspan="3">Net total</td><td style="text-align:right">${fmt(d.totals.taxable)}</td><td style="text-align:right">${fmt(d.totals.totalTax)}</td><td style="text-align:right">${fmt(d.totals.value)}</td></tr></tfoot>
</table>
<p style="margin-top:36px;font-size:11px;color:#aaa">Generated by BigxBook Desktop on ${new Date().toLocaleString('en-IN')}</p>
</body></html>`;
}
